// The signed-in session: the Oase token, the permissions decoded from it,
// and the MSAL account. One tiny external store (after common-shell's
// core/store.ts), read with useSyncExternalStore (ADR 0005).
//
// Storage is SHARED with the portal (ADR 0017): `oaseAccessToken` is written
// exactly as the portal's jotai atomWithStorage writes it — JSON-serialised,
// so the stored value is the token in double quotes.
import { useSyncExternalStore } from 'react'
import { decodePermissions, type Permission } from './permissions'

export const TOKEN_KEY = 'oaseAccessToken'
/** Prefix for every localStorage key this app owns (ADR 0017). */
export const APP_KEY_PREFIX = 'oase-admin:'

export interface SessionAccount {
  /** MSAL localAccountId. */
  id: string
  /** MSAL username (UPN/e-mail): the portal's `fullName`. */
  name: string
}

export interface Session {
  token: string | null
  permissions: Permission[]
  account: SessionAccount | null
}

function readStoredToken(): string | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'string' && parsed ? parsed : null
  } catch {
    return null
  }
}

function writeStoredToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, JSON.stringify(token))
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    // storage unavailable: the session still works in memory
  }
}

let state: Session = { token: null, permissions: [], account: null }
const listeners = new Set<() => void>()

function emit(next: Session) {
  if (next === state) return
  state = next
  listeners.forEach((listener) => {
    listener()
  })
}

export function getSession(): Session {
  return state
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useSession(): Session {
  return useSyncExternalStore(subscribeSession, getSession, getSession)
}

/** Set (or clear) the Oase token; permissions are re-decoded from it. */
export function setToken(token: string | null, options: { persist?: boolean } = {}) {
  if (options.persist !== false) writeStoredToken(token)
  if (token === state.token) return
  emit({ ...state, token, permissions: token ? decodePermissions(token) : [] })
}

export function setAccount(account: SessionAccount | null) {
  if (account?.id === state.account?.id && account?.name === state.account?.name) return
  emit({ ...state, account })
}

/** Load the stored token into memory (call once at boot). */
export function restoreSession() {
  setToken(readStoredToken(), { persist: false })
}

/**
 * Forget everything this app knows about the session, without
 * localStorage.clear(): only our token key and our own `oase-admin:*` keys
 * (ADR 0017). MSAL clears its own cache in logoutRedirect.
 */
export function clearSession() {
  writeStoredToken(null)
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key?.startsWith(APP_KEY_PREFIX)) localStorage.removeItem(key)
    }
  } catch {
    // ignore
  }
  emit({ token: null, permissions: [], account: null })
}

/**
 * Follow sign-in/sign-out in other tabs and in the portal (same origin):
 * the `storage` event fires on `oaseAccessToken` changes made elsewhere.
 */
export function watchSharedSession(): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== TOKEN_KEY && event.key !== null) return
    setToken(readStoredToken(), { persist: false })
  }
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener('storage', onStorage)
  }
}
